'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  motion,
  MotionValue,
  useMotionValueEvent,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import { PortableText } from 'next-sanity';
import { GroqImage } from '@/helpers/Image';

import { richTextComponent } from '@/sanity/sanityPortableText';
import { cn } from '@/lib/utils';
import AutoScroll from 'embla-carousel-auto-scroll';
import { Carousel, CarouselContent, CarouselItem } from '@/shadcn/ui/carousel';
import { PageFabioAffiliateSchema } from '@/sanity/pages/PageFabioAffiliateTemplate';
import { PortableTextBlock } from 'sanity';
import { CTAButton } from '@/shadcn/ui/button';

const ease = [0.25, 0.1, 0.25, 1] as const;

const TESTIMONIALS_BASE_HEIGHT_REM = 148.625;
const TESTIMONIALS_STEP_HEIGHT_REM = 90;
const STAR_OPACITIES = [
  'opacity-10',
  'opacity-40',
  'opacity-60',
  'opacity-80',
  'opacity-100',
];

function RatingDots({ dark = false }: { dark?: boolean }) {
  return (
    <div className="flex justify-between items-center w-[7.875rem]">
      {STAR_OPACITIES.map((opacity, index) => (
        <span
          key={index}
          className={cn(
            'h-[0.75rem] w-[0.75rem] shrink-0',
            dark ? 'bg-[#003828]' : 'bg-white',
            opacity,
          )}
        />
      ))}
    </div>
  );
}

function CurrentRating({
  label,
  reverse,
}: {
  label: string | null;
  reverse?: boolean;
}) {
  if (!label) return null;

  return (
    <div
      className={`inline-flex items-center gap-[2.625rem] ${reverse && 'flex-row-reverse'}`}
    >
      <RatingDots />
      <p className="mb-0 font-semibold tabular-nums whitespace-nowrap font-geist-mono text-[2rem] leading-[1.33] text-white/70">
        {label}
      </p>
    </div>
  );
}

function QuoteIcon() {
  return (
    <svg
      className="w-[4rem] h-[3rem] shrink-0"
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="24"
      fill="none"
      viewBox="0 0 32 24"
    >
      <path
        fill="#12F0B4"
        d="M18.69 24v-9.855C18.69 6.54 23.663 1.385 30.666 0l1.326 2.868c-3.242 1.223-5.326 4.85-5.326 7.799H32V24zM0 24v-9.855C0 6.54 4.997 1.384 12 0l1.328 2.868C10.084 4.091 8 7.718 8 10.667h5.31V24z"
      ></path>
    </svg>
  );
}

function VerifiedCheck() {
  return (
    <svg
      aria-hidden="true"
      className="text-white h-[1rem] w-[1.375rem] shrink-0"
      viewBox="0 0 12 8"
      fill="none"
    >
      <path
        d="M1 4L4.4 7L11 1"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1"
      />
    </svg>
  );
}
interface TestimonialCard {
  date?: string;
  quote?: PortableTextBlock[];
  name?: string;
  role?: string;
  verifiedLabel?: string;
}
function TestimonialCard({
  card,
  isActive,
  index,

  stackOffsetPx,
  cardHeightsPx,
  isMobile,
  measureRef,
}: {
  card: TestimonialCard;
  isActive: boolean;
  index: number;
  // backgroundImage?: PageHomeSchema["testimonialsBackgroundImage"];
  // progress: MotionValue< number>;
  stackOffsetPx: number;
  cardHeightsPx: number[];
  isMobile: boolean;
  measureRef: (el: HTMLElement | null) => void;
}) {
  return (
    <motion.article
      ref={measureRef}
      transition={{ duration: 0.45, ease }}
      className={cn(
        'absolute left-0 top-0 w-[50rem] lg:w-[78.375rem] overflow-hidden rounded-[0.25rem] border border-black/10 shadow-[0_4.75rem_14.0625rem_rgba(0,54,40,0.23)]',
        isActive ? 'z-30' : 'z-10',
        !isActive ? 'pointer-events-none' : '',
      )}
    >
      <motion.div className="absolute inset-y-0 left-0 origin-top w-[0.375rem] bg-[#12f0b4]" />

      <div className="flex relative flex-col h-full gap-[5.88rem] px-[7.5rem] pb-[4.25rem] pt-[4.25rem]">
        <div className="flex justify-between items-end">
          <div className="flex items-end gap-[1.75rem]">
            <QuoteIcon />
            {card.date && (
              <p className="mb-0 font-semibold uppercase font-geist text-[#12f0b4]/40 text-[1.75rem] leading-[1.33]">
                {card.date}
              </p>
            )}
          </div>

          <div className="flex items-center gap-[1.5rem] rounded-[0.25rem] bg-[#45917c] px-[1.5rem] py-[0.5rem]">
            <VerifiedCheck />
            <p className="mb-0 font-bold text-white font-geist text-[1.5rem] leading-[1.33]">
              {card.verifiedLabel || 'VERIFIED'}
            </p>
          </div>
        </div>

        {card.quote && (
          <div className="richtext richtext-start lg:w-[56.5rem] prose-p:text-[3rem] prose-p:font-normal prose-p:leading-[1.3] prose-p:tracking-[-0.09rem] prose-p:text-white prose-headings:text-[3rem] prose-headings:font-normal prose-headings:leading-[1.3] prose-headings:tracking-[-0.09rem] prose-headings:text-white">
            <PortableText value={card.quote} components={richTextComponent} />
          </div>
        )}

        <div className="flex items-center mt-auto gap-[2.5rem]">
          {card.name && (
            <p className="mb-0 font-semibold text-white uppercase font-geist-mono text-[2rem] leading-[1.33]">
              {card.name}
            </p>
          )}
          <span className="w-px h-[3.452rem] bg-white/20" />
          {card.role && (
            <p className="mb-0 font-semibold uppercase font-geist-mono text-[1.75rem] leading-[1.33] text-white/40">
              {card.role}
            </p>
          )}
        </div>

        <div className="absolute left-[26.31625rem] top-[19.33375rem]">
          <RatingDots dark />
        </div>
      </div>
    </motion.article>
  );
}

export default function TestimonialsSection({
  data,
}: {
  data: PageFabioAffiliateSchema;
}) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const cards = data?.testimonialsItems ?? [];
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [stackOffsetPx, setStackOffsetPx] = useState(40 * 8);
  const [isMobile, setIsMobile] = useState(false);

  const cardEls = useRef<(HTMLElement | null)[]>([]);
  const [cardHeightsPx, setCardHeightsPx] = useState<number[]>([]);

  if (!data?.testimonialsHeading && !cards.length) return null;
  const ratingLabel =
    cards.length > 0
      ? `${String(currentCardIndex + 1).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`
      : null;
  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-x-clip px-[1rem]"
    >
      <div className="h-full bg-[#001B14] py-[8rem] lg:py-[15rem]">
        <div className="mx-auto flex flex-col w-[min(147.5rem,calc(100%-4rem))] items-center justify-between gap-[6rem] lg:gap-[13rem]  mb-[5rem]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.8, ease }}
            className="flex flex-col items-center"
            style={{ willChange: 'transform' }}
          >
            {data?.testimonialsChip && (
              <p className="italic text-center mb-[1rem] font-blacker text-[2rem] leading-[1.5] text-white/60">
                {data.testimonialsChip}
              </p>
            )}

            {data?.testimonialsHeading && (
              <div className="w-full richtext prose-em:font-normal prose-em:text-[3.75rem] prose-em:tracking-normal lg:prose-em:text-[8rem] prose-headings:text-white  prose-headings:tracking-[-0.25rem] prose-headings:font-[500] lg:prose-headings:tracking-[-0.4rem]">
                <PortableText
                  value={data.testimonialsHeading}
                  components={richTextComponent}
                />
              </div>
            )}

            <div className="mt-[5rem]">
              <CurrentRating label={data.testimonialsRating} reverse />
            </div>

            {data?.testimonialsRatingText && (
              <div className="richtext mt-[1.75rem] w-[43.5rem] prose-p:text-[2rem] prose-p:font-normal prose-p:leading-[1.33] prose-p:text-white/70 prose-strong:font-semibold prose-strong:text-white">
                <PortableText
                  value={data.testimonialsRatingText}
                  components={richTextComponent}
                />
              </div>
            )}
          </motion.div>
        </div>

        {data?.testimonialsItems?.length > 0 &&
          (() => {
            const accentColors = ['#FFF9A8', '#FFDDE8', '#DAFFE1'] as const;
            const accentBgs = ['#FFF9A826', '#FFDDE826', '#DAFFE126'] as const;

            const renderCard = (
              t: (typeof data.testimonialsItems)[0],
              i: number,
            ) => {
              const accent = accentColors[i % 3];
              const accentBg = accentBgs[i % 3];
              return (
                <article
                  key={i}
                  className="flex flex-col h-full border gap-[1.5rem] rounded-[0.25rem] border-white/10 bg-[#112E27] p-[3rem] w-[42rem] lg:w-[50rem]"
                >
                  {/* Initials / avatar — top */}
                  {t?.avatar ? (
                    <GroqImage
                      src={t.avatar as any}
                      alt={t.name ?? ''}
                      width={40}
                      height={40}
                      className="object-cover rounded-full h-[6rem] w-[6rem]"
                    />
                  ) : (
                    t?.initials && (
                      <div
                        className="flex justify-center items-center font-semibold rounded-[0.5rem] h-[6rem] w-[6rem] text-[2.25rem]"
                        style={{ backgroundColor: accentBg, color: accent }}
                      >
                        {t.initials}
                      </div>
                    )
                  )}

                  {/* Quote — middle */}
                  {t?.quote && (
                    <div className="flex-1 mt-[1rem] prose-p:text-[2rem] prose-p:text-white lg:prose-p:text-[2.25rem]">
                      <PortableText
                        value={t.quote}
                        components={richTextComponent}
                      />
                    </div>
                  )}

                  {/* Stars + name — bottom, space-between */}
                  <div className="flex flex-col justify-between items-start lg:flex-row lg:items-center gap-[1rem]">
                    {t?.stars && (
                      <span
                        className="text-[2rem] lg:text-[2.25rem]"
                        style={{ color: accent }}
                      >
                        {t.stars}
                      </span>
                    )}
                    {t?.name &&
                      (() => {
                        const [left, ...rest] = t.name.split('\u2014');
                        const right = rest.join('\u2014');
                        return (
                          <span className="font-semibold text-[2rem] lg:text-[2.25rem]">
                            <span style={{ color: accent }}>{left.trim()}</span>
                            {right && (
                              <span className="text-white">
                                {' \u2014 '}
                                {right.trim()}
                              </span>
                            )}
                          </span>
                        );
                      })()}
                  </div>
                </article>
              );
            };

            const row1 = data.testimonialsItems.filter((_, i) => i % 2 === 0);
            const row2 = data.testimonialsItems.filter((_, i) => i % 2 !== 0);
            // original indices so colours stay consistent
            const row1Indices = data.testimonialsItems
              .map((_, i) => i)
              .filter((i) => i % 2 === 0);
            const row2Indices = data.testimonialsItems
              .map((_, i) => i)
              .filter((i) => i % 2 !== 0);

            return (
              <div className="flex overflow-hidden relative flex-col w-full h-full gap-[2rem]">
                {/* Row 1 — scrolls right (forward) */}
                <div className="relative w-full">
                  <div className="absolute inset-y-0 left-0 bg-gradient-to-r to-transparent pointer-events-none w-[4rem] from-[#001B14] z-[1] lg:w-[35rem]" />
                  <div className="absolute inset-y-0 right-0 bg-gradient-to-l to-transparent pointer-events-none w-[4rem] from-[#001B14] z-[1] lg:w-[35rem]" />
                  <Carousel
                    opts={{ loop: true, dragFree: true, align: 'start' }}
                    plugins={[
                      AutoScroll({
                        speed: 0.8,
                        stopOnInteraction: false,
                        stopOnMouseEnter: true,
                      }),
                    ]}
                    className="w-full"
                  >
                    <CarouselContent className="-ml-[2rem]">
                      {[...row1, ...row1, ...row1].map((t, idx) => (
                        <CarouselItem
                          key={idx}
                          className="pl-[2rem] basis-auto"
                        >
                          {renderCard(t, row1Indices[idx % row1Indices.length])}
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>
                </div>

                {/* Row 2 — scrolls left (backward) */}
                <div className="relative w-full">
                  <div className="absolute inset-y-0 left-0 bg-gradient-to-r to-transparent pointer-events-none w-[4rem] from-[#001B14] z-[1] lg:w-[22rem]" />
                  <div className="absolute inset-y-0 right-0 bg-gradient-to-l to-transparent pointer-events-none w-[4rem] from-[#001B14] z-[1] lg:w-[22rem]" />
                  <Carousel
                    opts={{ loop: true, dragFree: true, align: 'start' }}
                    plugins={[
                      AutoScroll({
                        speed: 0.8,
                        direction: 'backward',
                        stopOnInteraction: false,
                        stopOnMouseEnter: true,
                      }),
                    ]}
                    className="w-full"
                  >
                    <CarouselContent className="-ml-[2rem]">
                      {[...row2, ...row2, ...row2].map((t, idx) => (
                        <CarouselItem
                          key={idx}
                          className="pl-[2rem] basis-auto"
                        >
                          {renderCard(t, row2Indices[idx % row2Indices.length])}
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>
                </div>
              </div>
            );
          })()}

        {data?.testimonialsCTA && (
          <div className="flex justify-center items-center mt-[9.25rem] px-[4rem]">
            <CTAButton data={data.testimonialsCTA} />
          </div>
        )}
      </div>
    </section>
  );
}
